import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const PermissionsContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// IDENTITY M2b — DELEGATED-ONLY permissions (mirror of backend rbac_api._DELEGATED_ONLY_PERMS for the
// six M2a perms that have a menu item). The backend's /rbac/my-permissions CARVES THESE OUT of the
// `permissions` list for every privileged user who was not EXPLICITLY granted them (only is_master holds
// them implicitly, and even for is_master they are absent from the list — is_master is signalled by
// is_admin + the user flag). So a delegated perm must NEVER be satisfied by the generic isAdmin/isOwner
// short-circuit — only by is_master OR actual membership in `permissions`. This makes the menu HONEST:
// an admin (e.g. Savannah) stops seeing Security/Settings links she cannot actually use, while the owner
// (Shar) who was granted them still sees them. Mirrors backend delegated_access.has_delegated_access.
export const DELEGATED_ONLY_PERMS = new Set([
  'payroll.run', 'payroll.view',
  'security.view', 'security.manage',
  'settings.view', 'settings.manage',
  // Phone system — delegated-only (Wave 2, Nov 2026). Mirror of backend phone_api perms.
  'phone.use', 'phone.inbound',
  'phone.view_all_calls',
  'phone.recordings.view', 'phone.recordings.download',
  'phone.sms.send',
  'phone.settings.manage',
]);

export function PermissionsProvider({ children }) {
  const [permissions, setPermissions] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [tokenKey, setTokenKey] = useState(localStorage.getItem('auth_token') || '');

  const fetchPermissions = useCallback(async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) { setLoaded(true); return; }
    try {
      const res = await fetch(`${API_URL}/api/rbac/my-permissions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPermissions(data.permissions || []);
        setGroupName(data.group_name || '');
        setIsAdmin(data.is_admin || false);
      }
    } catch (e) { console.error('Failed to fetch permissions:', e); }
    finally { setLoaded(true); }
  }, []);

  // Re-fetch when token changes (login/logout)
  useEffect(() => {
    const interval = setInterval(() => {
      const current = localStorage.getItem('auth_token') || '';
      if (current !== tokenKey) {
        setTokenKey(current);
        fetchPermissions();
      }
    }, 500);
    return () => clearInterval(interval);
  }, [tokenKey, fetchPermissions]);

  useEffect(() => { fetchPermissions(); }, [fetchPermissions]);

  const hasPerm = useCallback((perm) => {
    if (!loaded) return true; // Show everything while loading
    if (isAdmin) return true;
    return permissions.includes(perm);
  }, [permissions, isAdmin, loaded]);

  const hasAnyPerm = useCallback((perms) => {
    if (!loaded) return true; // Show everything while loading
    if (isAdmin) return true;
    return perms.some(p => permissions.includes(p));
  }, [permissions, isAdmin, loaded]);

  // IDENTITY M2b — delegated-aware nav gate. Returns true if ANY of `perms` is satisfied, honoring the
  // delegated rule: a DELEGATED-ONLY perm passes ONLY via is_master or actual membership (never the
  // isAdmin/isOwner short-circuit); a normal perm keeps the existing isOwner/isAdmin/membership behavior.
  // Pass the caller's { isMaster, isOwner } (from useAuth) so this stays the single source of the rule.
  const navPermOk = useCallback((perms, { isMaster = false, isOwner = false } = {}) => {
    if (!loaded) return true; // show everything while loading (consistent with hasPerm/hasAnyPerm)
    const list = Array.isArray(perms) ? perms : [perms];
    return list.some((p) => {
      if (DELEGATED_ONLY_PERMS.has(p)) return !!isMaster || permissions.includes(p);
      return !!isMaster || !!isOwner || isAdmin || permissions.includes(p);
    });
  }, [permissions, isAdmin, loaded]);

  return (
    <PermissionsContext.Provider value={{ permissions, groupName, isAdmin, loaded, hasPerm, hasAnyPerm, navPermOk, refetch: fetchPermissions }}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const ctx = useContext(PermissionsContext);
  if (!ctx) return { permissions: [], isAdmin: true, loaded: true, hasPerm: () => true, hasAnyPerm: () => true, navPermOk: () => true, refetch: () => {} };
  return ctx;
}
